import React, { FC, useMemo, useState, useCallback } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Theme } from '@mui/system';
import { useTheme } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'

import Button from '@mui/material/Button'
import AppBar from '@mui/material/AppBar'
import Toolbar from '@mui/material/Toolbar'
import Container from '@mui/material/Container'
import Typography from '@mui/material/Typography'
import Box from '@mui/material/Box'

import PrivacyPolicy from '../components/widgets/PrivacyPolicy'
import TermsAndConditions from '../components/widgets/TermsAndConditions'
import Link from '../components/widgets/Link'
import Paper from '@mui/material/Paper'
import Snackbar from '../components/system/Snackbar'
import GlobalLoading from '../components/system/GlobalLoading'

import UploadedImage from '@kaidam/fc-frontend/src/components/widgets/UploadedImage'

import routerActions from '@kaidam/fc-shared/src/frontend/store/modules/router'
import routerSelectors from '@kaidam/fc-shared/src/frontend/store/selectors/router'
import networkSelectors from '@kaidam/fc-shared/src/frontend/store/selectors/network'
import authSelectors from '../store/selectors/auth'

import useOrgs from '../hooks/useOrgs'
import useConfig from '../hooks/useConfig'
import useFullRoute from '../hooks/useFullRoute'

import useFileUrl from '@kaidam/fc-frontend/src/components/hooks/useFileUrl'

import {
  IData_Org_With_Profile,
} from '@kaidam/fc-shared/src/types/rbac'

import {
  APP_LOGO,
  APP_TITLE,
  TOPBAR_HEIGHT,
} from '../settings'

import MenuButton from '@kaidam/fc-frontend/src/components/widgets/MenuButton'
import { MenuButtonItem } from '@kaidam/fc-frontend/src/types/app'

const Layout: FC<React.PropsWithChildren<unknown>> = ({
  children,
}) => {
  const dispatch = useDispatch()
  const orgs = useOrgs()
  const config = useConfig()
  const theme = useTheme()
  
  const params = useSelector(routerSelectors.params)
  const globalLoading = useSelector(networkSelectors.globalLoading)
  const {
    route,
    fullRoute,
  } = useFullRoute()

  const isMobile = useMediaQuery(theme.breakpoints.down('md'))

  const openWindow = useCallback((name: string) => {
    dispatch(routerActions.mergeQueryParams({
      viewingWindow: name,
    }))
  }, [
    route,
  ])

  const closeWindow = useCallback(() => {
    dispatch(routerActions.removeQueryParams(['viewingWindow']))
  }, [
    route,
  ])

  const org = useMemo<IData_Org_With_Profile | undefined>(() => {
    const id = params.orgid
    if(!id) return undefined
    return orgs.find(org => org.id == id)
  }, [
    orgs,
    params,
  ])
  
  const logoOrg = useMemo(() => {
    return orgs.find(o => {
      if (o.id == params.orgid) return true
      if (o.profile.website_url == params.orgid) return true
      return false
    })
  }, [
    orgs,
    params,
  ])

  const getMenuItems = useCallback(() => {
    const items: MenuButtonItem[] = [
      {
        title: 'Main Website',
        handler: () => {
          document.location = 'https://cheeseproject.co.uk'
        }
      }
    ]

    if (fullRoute.meta?.showManage && !config.isOffSeason) {
      items.push({
        title: 'Manage Existing Booking',
        handler: () => {
          dispatch(routerActions.navigateTo('login'))
        }
      })
    }

    if (config.isOffSeason) {
      items.push({
        title: 'Register Interest In A Survey',
        handler: () => {
          dispatch(routerActions.navigateTo('register_interest', {is_off_season:'yes'}))
        }
      })
    }

    if (fullRoute.meta?.showLogout) {
      items.push({
        title: 'Logout',
        handler: () => {
          localStorage.removeItem('manage_token')
          dispatch(routerActions.navigateTo('login'))
        }
      })
    }

    return items
  }, [fullRoute.meta, config.isOffSeason])

  return (
    <Box sx={{
      minHeight: '100%',
      backgroundColor: '#72428A',
    }}>
      <AppBar sx={{
        backgroundColor: '#FFFFFF',
        flexGrow: 1,
        flex: 1,
        height: `${TOPBAR_HEIGHT}px`,
      }}>
        <Toolbar sx={{
          pl: 2,
          pr: '0px',
          backgroundColor: '#FFFFFF',
          height: '100%'
        }}>
          <Box sx={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
          }}>
            <Link name="home">
              <Box component="img" src="/img/logos/CHEESE_house_small.jpg" sx={{
                ml: 2,
                height: '70px',
              }} />
            </Link>
            {!isMobile && (
              <Typography variant="h6" sx={{
                ml: 2,
                color: '#666',
              }}>
                Booking Service
              </Typography>
            )}
          </Box>
          <Box sx={{ ml: 2, flex: 1 }} />
          {isMobile ? (
            <MenuButton
              getItems={getMenuItems}
            />
          ) : (
            <>
              <Box sx={{
                flexGrow: 0,
                pr: 2,
                color: theme => theme.palette.grey[700],
              }}>
                <Button
                  variant="outlined"
                  onClick={() => {
                    document.location = 'https://cheeseproject.co.uk'
                  }}
                >
                  Main Website
                </Button>
              </Box>
              {fullRoute.meta?.showManage && !config.isOffSeason && (
                <Box sx={{
                  flexGrow: 0,
                  pr: 2,
                  color: theme => theme.palette.grey[700],
                }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      dispatch(routerActions.navigateTo('login'))
                    }}
                  >
                    Manage Existing Booking
                  </Button>
                </Box>
              )}
              {config.isOffSeason && (
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => {
                    dispatch(routerActions.navigateTo('register_interest', {is_off_season:'yes'}))
                  }}
                >
                  Register Interest In A Survey
                </Button>
              )}
              {fullRoute.meta?.showLogout && (
                <Box sx={{
                  flexGrow: 0,
                  pr: 2,
                  color: theme => theme.palette.grey[700],
                }}>
                  <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                      localStorage.removeItem('manage_token')
                      dispatch(routerActions.navigateTo('login'))
                    }}
                  >
                    Logout
                  </Button>
                </Box>
              )}
            </>
          )}
        </Toolbar>
      </AppBar>
      <Box sx={{
        pt: `${TOPBAR_HEIGHT}px`,
        height: '100%',
        zIndex: 100,
        position: 'relative',
      }}>
        {children}
      </Box>
      {org && (
        <Container maxWidth="lg" sx={{
          zIndex: 100,
          position: 'relative',
          p: 2,
          pt: 0,
        }}>
          <Paper sx={{
            width: '100%',
            p: 3,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            justifyContent: 'flex-end',
          }}>
            <Box
              component="a"
              href="#"
              onClick={() => openWindow('privacy')}
              sx={{
                color: '#000',
                textDecoration: 'none',
              }}
            >
              <Typography variant="body2">
                Privacy Notice
              </Typography>
            </Box>
            &nbsp;|&nbsp;
            <Box
              component="a"
              href="#"
              onClick={() => openWindow('terms')}
              sx={{
                color: '#000',
                textDecoration: 'none',
              }}
            >
              <Typography variant="body2">
                Terms and Conditions
              </Typography>
            </Box>
          </Paper>
        </Container>
      )}
      {params.viewingWindow == 'privacy' && org && (
        <PrivacyPolicy
          org={org}
          onClose={() => closeWindow()}
        />
      )}
      {params.viewingWindow == 'terms' && org && (
        <TermsAndConditions
          org={org}
          onOpenPrivacy={() => openWindow('privacy')}
          onClose={() => closeWindow()}
        />
      )}
      <GlobalLoading {...globalLoading} />
      <Snackbar />
    </Box>
  )
}

export default Layout
